import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import GroupsIcon from "@mui/icons-material/Groups";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import apiEndpoints from "../../../apiconfig";

export default function MarkStaffAttendanceUI() {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const getToday = () => new Date().toISOString().split("T")[0];

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const [date, setDate] = useState(getToday);
  const [staffList, setStaffList] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);

  const [reasonDialog, setReasonDialog] = useState({
    open: false,
    staff_guid: null,
    status: "",
    reason: "",
  });

  const [editStaff, setEditStaff] = useState(null);

  /* -----------------------------------------------------
     FETCH STAFF LIST (on load + date change)
  ----------------------------------------------------- */
  useEffect(() => {
    if (!date) return;

    const controller = new AbortController();
    setLoading(true);

    fetch(`${apiEndpoints.staffAttendance}?action=staff-list&date=${date}`, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((res) => {
        if (!res.status) {
          showSnackbar(res.message, "error");
          setStaffList([]);
          setLoading(false);
          return;
        }

        const staff = res.data?.data || [];
        setStaffList(staff);

        // Initialize attendance
        const init = {};
        staff.forEach((s) => {
          init[s.guid] = {
            status: s.status || "present",
            reason: s.remarks || s.reason || "",
            check_in: s.check_in ? s.check_in.substring(0, 5) : "",
            check_out: s.check_out ? s.check_out.substring(0, 5) : "",
          };
        });

        setAttendance(init);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          showSnackbar("Failed to load staff", "error");
        }
        setLoading(false);
      });

    return () => controller.abort();
  }, [date]);

  /* -----------------------------------------------------
     STATUS HANDLER
  ----------------------------------------------------- */
  const setStatus = (guid, status) => {
    if (status === "absent") {
      setReasonDialog({
        open: true,
        staff_guid: guid,
        status: "absent",
        reason: "",
      });
      return;
    }

    setAttendance((prev) => ({
      ...prev,
      [guid]: {
        ...prev[guid],
        status: "present",
        reason: "",
      },
    }));
  };

  const saveSingleStaffAttendance = (staffGuid, updatedAtt) => {
    if (!date) return;

    const record = {
      staff_guid: staffGuid,
      status: updatedAtt.status || "present",
      remarks: updatedAtt.reason || "",
      check_in: updatedAtt.check_in || "",
      check_out: updatedAtt.check_out || "",
    };

    fetch(`${apiEndpoints.staffAttendance}?action=insert`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
      body: JSON.stringify({
        date,
        records: [record],
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (!res.status) {
          showSnackbar(res.message || "Failed to auto-save staff attendance", "error");
        }
      })
      .catch(() => {
        showSnackbar("Network error saving staff attendance", "error");
      });
  };

  /* -----------------------------------------------------
     SUBMIT ATTENDANCE
  ----------------------------------------------------- */
  const submitAttendance = () => {
    if (!staffList.length) return;

    const records = staffList.map((s) => ({
      staff_guid: s.guid,
      status: attendance[s.guid]?.status || "present",
      remarks: attendance[s.guid]?.reason || "",
      check_in: attendance[s.guid]?.check_in || "",
      check_out: attendance[s.guid]?.check_out || "",
    }));

    fetch(`${apiEndpoints.staffAttendance}?action=insert`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
      body: JSON.stringify({
        date,
        records,
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (!res.status) {
          showSnackbar(res.message, "error");
          return;
        }

        showSnackbar("Staff attendance saved successfully", "success");

        setTimeout(() => {
          navigate("/admin/staff-viewattendance");
        }, 800);
      })
      .catch(() => {
        showSnackbar("Something went wrong", "error");
      });
  };

  /* -----------------------------------------------------
     LOADING STATE
  ----------------------------------------------------- */
  if (loading) {
    return <Typography sx={{ p: 3 }}>Loading staff...</Typography>;
  }

  /* -----------------------------------------------------
     UI
  ----------------------------------------------------- */
  return (
    <Box
      sx={{
        p: 3,
        minHeight: "100vh",
        background: "rgba(255,255,255,0.35)",
        backdropFilter: "blur(10px)",
      }}
    >
      <Typography variant="h4" fontWeight={700} mb={3}>
        <GroupsIcon sx={{ mr: 1 }} />
        Staff Attendance
      </Typography>

      {/* DATE CARD */}
      <Card sx={{ p: 3, mb: 4, borderRadius: 4 }}>
        <Typography fontWeight={600} mb={1}>
          Attendance Date
        </Typography>
        <TextField
          type="date"
          fullWidth
          value={date}
          onChange={(e) => setDate(e.target.value)}
          inputProps={{ max: getToday() }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "999px",
            },
          }}
        />
      </Card>

      {/* STAFF LIST */}
      <Card sx={{ p: 3, borderRadius: 4 }}>
        <Typography variant="h6" fontWeight={700} mb={2}>
          Mark Staff Attendance
        </Typography>

        {staffList.length === 0 ? (
          <Typography>No staff available</Typography>
        ) : (
          <Grid container spacing={2}>
            {staffList.map((staff) => {
              const att = attendance[staff.guid] || { status: "present", check_in: "", check_out: "" };
              const isPresent = att.status === "present";

              // Calculate total hours dynamically
              let displayHours = "";
              if (isPresent && att.check_in && att.check_out) {
                const [h1, m1] = att.check_in.split(":").map(Number);
                const [h2, m2] = att.check_out.split(":").map(Number);
                const diffMinutes = (h2 * 60 + m2) - (h1 * 60 + m1);
                if (diffMinutes > 0) {
                  displayHours = (diffMinutes / 60).toFixed(2) + " hrs";
                } else {
                  displayHours = "0 hrs";
                }
              }

              return (
                <Grid item xs={12} key={staff.guid}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      justifyContent: "space-between",
                      alignItems: { xs: "stretch", md: "center" },
                      background: "rgba(255,255,255,0.75)",
                      border: `1px solid ${isPresent ? "rgba(76, 175, 80, 0.12)" : "rgba(244, 67, 54, 0.12)"}`,
                      gap: 2
                    }}
                  >
                    <Box sx={{ minWidth: 200 }}>
                      <Typography fontWeight={600}>{staff.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {staff.role}
                      </Typography>
                    </Box>

                    <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                      {!isPresent && (
                        <Chip
                          label="Absent"
                          color="error"
                          variant="outlined"
                          size="small"
                          sx={{ borderRadius: 2 }}
                        />
                      )}

                      {isPresent && (
                        <Box display="flex" alignItems="center" gap={1.5}>
                          {!att.check_in && (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              sx={{ borderRadius: 2 }}
                              onClick={() => {
                                const newTime = getCurrentTime();
                                const newAtt = { ...att, check_in: newTime };
                                setAttendance((prev) => ({
                                  ...prev,
                                  [staff.guid]: newAtt
                                }));
                                saveSingleStaffAttendance(staff.guid, newAtt);
                              }}
                            >
                              Check-In
                            </Button>
                          )}

                          {att.check_in && !att.check_out && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip label={`In: ${att.check_in}`} variant="outlined" size="small" />
                              <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                sx={{ borderRadius: 2 }}
                                onClick={() => {
                                  const newTime = getCurrentTime();
                                  const newAtt = { ...att, check_out: newTime };
                                  setAttendance((prev) => ({
                                    ...prev,
                                    [staff.guid]: newAtt
                                  }));
                                  saveSingleStaffAttendance(staff.guid, newAtt);
                                }}
                              >
                                Check-Out
                              </Button>
                            </Box>
                          )}

                          {att.check_in && att.check_out && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip label={`In: ${att.check_in}`} variant="outlined" size="small" />
                              <Chip label={`Out: ${att.check_out}`} variant="outlined" size="small" />
                              {displayHours && (
                                <Chip
                                  label={displayHours}
                                  color="primary"
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                            </Box>
                          )}

                          {(att.check_in || att.check_out) && (
                            <Button
                              variant="text"
                              color="error"
                              size="small"
                              onClick={() => {
                                const newAtt = { ...att, check_in: "", check_out: "" };
                                setAttendance((prev) => ({
                                  ...prev,
                                  [staff.guid]: newAtt
                                }));
                                saveSingleStaffAttendance(staff.guid, newAtt);
                              }}
                              sx={{ minWidth: "auto", p: 0.5 }}
                            >
                              Reset
                            </Button>
                          )}
                        </Box>
                      )}

                      {!isPresent && att.reason && (
                        <Typography variant="body2" color="error" sx={{ fontStyle: "italic" }}>
                          Reason: {att.reason}
                        </Typography>
                      )}

                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => setEditStaff(staff)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 4, py: 1.4, borderRadius: 3 }}
          onClick={submitAttendance}
          disabled={!staffList.length}
        >
          Save Attendance
        </Button>
      </Card>

      {/* REASON DIALOG */}
      <Dialog
        open={reasonDialog.open}
        onClose={() => setReasonDialog({ open: false, staff_guid: null })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reason Required</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Enter reason..."
            value={reasonDialog.reason}
            onChange={(e) =>
              setReasonDialog((prev) => ({
                ...prev,
                reason: e.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReasonDialog({ open: false, staff_guid: null })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!reasonDialog.reason.trim()) return;

              setAttendance((prev) => ({
                ...prev,
                [reasonDialog.staff_guid]: {
                  ...prev[reasonDialog.staff_guid],
                  status: "absent",
                  reason: reasonDialog.reason,
                  check_in: "",
                  check_out: "",
                },
              }));

              setReasonDialog({
                open: false,
                staff_guid: null,
                reason: "",
              });
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* ADVANCED EDIT DIALOG */}
      <Dialog
        open={Boolean(editStaff)}
        onClose={() => {
          if (editStaff) {
            saveSingleStaffAttendance(editStaff.guid, attendance[editStaff.guid]);
          }
          setEditStaff(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Attendance - {editStaff?.name}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
          <Box display="flex" gap={2}>
            <Button
              variant={attendance[editStaff?.guid]?.status === "present" ? "contained" : "outlined"}
              color="success"
              fullWidth
              onClick={() =>
                setAttendance((prev) => ({
                  ...prev,
                  [editStaff.guid]: {
                    ...prev[editStaff.guid],
                    status: "present",
                    reason: ""
                  }
                }))
              }
            >
              Present
            </Button>
            <Button
              variant={attendance[editStaff?.guid]?.status === "absent" ? "contained" : "outlined"}
              color="error"
              fullWidth
              onClick={() =>
                setAttendance((prev) => ({
                  ...prev,
                  [editStaff.guid]: {
                    ...prev[editStaff.guid],
                    status: "absent",
                    check_in: "",
                    check_out: ""
                  }
                }))
              }
            >
              Absent
            </Button>
          </Box>

          {attendance[editStaff?.guid]?.status === "present" && (
            <Box display="flex" gap={2}>
              <TextField
                type="time"
                label="Check In"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={attendance[editStaff?.guid]?.check_in || ""}
                onChange={(e) =>
                  setAttendance((prev) => ({
                    ...prev,
                    [editStaff.guid]: {
                      ...prev[editStaff.guid],
                      check_in: e.target.value
                    }
                  }))
                }
              />
              <TextField
                type="time"
                label="Check Out"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={attendance[editStaff?.guid]?.check_out || ""}
                onChange={(e) =>
                  setAttendance((prev) => ({
                    ...prev,
                    [editStaff.guid]: {
                      ...prev[editStaff.guid],
                      check_out: e.target.value
                    }
                  }))
                }
              />
            </Box>
          )}

          {attendance[editStaff?.guid]?.status === "absent" && (
            <TextField
              label="Reason for Absence"
              multiline
              rows={3}
              fullWidth
              value={attendance[editStaff?.guid]?.reason || ""}
              onChange={(e) =>
                setAttendance((prev) => ({
                  ...prev,
                  [editStaff.guid]: {
                    ...prev[editStaff.guid],
                    reason: e.target.value
                  }
                }))
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              saveSingleStaffAttendance(editStaff.guid, attendance[editStaff.guid]);
              setEditStaff(null);
            }} 
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
